import {
  GDTMessageStatus,
  HL7MessageDirection,
  HL7MessageType,
  QueryFactory,
} from '@lmt/shared-types';
import { DatePicker } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/date-time/DatePicker';
import { Input } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { HL7MessageTypeNames } from '@lmt/unicorn-shared/data/messageTypeNames';
import React, { useEffect, useMemo } from 'react';
import { DropDownInput } from '../../../common/ui-kit/inputs/dropdown/DropDownInput';
import { getHL7MessageStatuses } from './common';
import styles from './MessagesFilter.module.scss';
import { Field } from '../../../common/Field';
import { Button, ButtonColor } from '../../../common/ui-kit/buttons/Button';
import {
  PatientSearch,
  SearchInputPatientData,
} from '../../../common/patient/PatientSearch';

type Props = {
  search: string | null;
  from: Date | null;
  to: Date | null;
  messageType: HL7MessageType | null;
  direction: HL7MessageDirection | null;
  status: GDTMessageStatus | null;
  patientId: number | null;
  searchKey: number;
  searchByError: string | null;
  onFromChanged: (date: Date | null) => void;
  onToChanged: (date: Date | null) => void;
  onStatusChanged: (status: GDTMessageStatus | null) => void;
  onDirectionChanged: (direction: HL7MessageDirection | null) => void;
  onMessageTypeChanged: (messageType: HL7MessageType | null) => void;
  onSearchChanged: (value: string) => void;
  onSearchByErrorChanged: (value: string) => void;
  onPatientChanged: (value: SearchInputPatientData | null) => void;
  onReset: () => void;
};
export const MessagesFilter: React.FC<Props> = React.memo(
  function MessagesFilter(props) {
    const i18n = useScopedTranslation('hl7.messages');
    const { data: patient, isFetching } =
      QueryFactory.PatientQuery.useGetPatientQuery(props.patientId!, {
        enabled: !!props.patientId,
      });

    const hl7MessageDirections = useMemo(() => {
      return [
        {
          value: HL7MessageDirection.Outgoing,
          displayValue: i18n.t('outgoing_message_status') as string,
        },
        {
          value: HL7MessageDirection.Incoming,
          displayValue: i18n.t('incoming_message_status') as string,
        },
      ].sort((a, b) => a.displayValue.localeCompare(b.displayValue));
    }, []);

    const hl7MessageStatuses = useMemo(() => {
      return getHL7MessageStatuses(props.direction);
    }, [props.direction]);

    useEffect(() => {
      if (props.status && !hl7MessageStatuses.find((x) => x == props.status)) {
        props.onStatusChanged(null);
      }
    }, [hl7MessageStatuses]);

    return (
      <div data-test-id="messages-filter" className={styles.container}>
        <div className={styles.header}>
          {(i18n.t('filter.title') as string).toUpperCase()}
        </div>
        <Field title={i18n.t('filter.message')}>
          <Input
            key={'message' + props.searchKey}
            placeholder={i18n.t('filter.search_placeholder')}
            onBlur={(e) => props.onSearchChanged(e.target.value)}
            onEnterPressed={(value) => props.onSearchChanged(value)}
            defaultValue={props.search ?? ''}
            variant={'normal'}
          />
        </Field>
        <Field title={i18n.t('filter.patient')}>
          <PatientSearch
            key={'patient' + props.searchKey}
            isOnlyActive={true}
            onSelectedPatientChanged={props.onPatientChanged}
            selectedPatient={props.patientId ? (patient ?? null) : null}
            patientsLimit={9}
            autoFocus={true}
            variant={'normal'}
            selectPatientIfOnlyOneFound={true}
            data-variant="normal"
            className={styles.patientSearch}
          />
        </Field>
        <Field title={i18n.t('filter.direction')}>
          <DropDownInput
            enableSearch={true}
            options={hl7MessageDirections}
            required={false}
            variant={'normal'}
            idFunction={'value'}
            getOptionLabel={'displayValue'}
            useIdFunctionAsValue={true}
            value={props.direction}
            onValueChanged={(option) => {
              const value = option?.value ?? null;
              if (props.direction === value) return;
              props.onDirectionChanged(value);
            }}
          />
        </Field>
        <Field title={i18n.t('filter.status')}>
          <DropDownInput
            enableSearch={true}
            options={hl7MessageStatuses}
            required={false}
            variant={'normal'}
            getOptionLabel={(x) => i18n.t(`status.${x}` as any)}
            useIdFunctionAsValue={true}
            value={props.status}
            onValueChanged={(option) => {
              const value = option ?? null;
              if (props.status === value) return;
              props.onStatusChanged(value);
            }}
          />
        </Field>
        <Field title={i18n.t('filter.message_type')}>
          <DropDownInput
            enableSearch={true}
            useVirtualization={true}
            options={HL7MessageTypeNames}
            required={false}
            variant={'normal'}
            getOptionLabel="short"
            idFunction="id"
            useIdFunctionAsValue={true}
            value={props.messageType}
            onValueChanged={(option) => {
              const id = option?.id ?? null;
              if (props.messageType === id) return;
              props.onMessageTypeChanged(id);
            }}
          />
        </Field>
        <Field title={i18n.t('filter.filter_from')}>
          <DatePicker
            key={'from' + props.searchKey}
            value={props.from}
            onDateChanged={props.onFromChanged}
            withTime={true}
            displaySeconds={true}
            isFormInput={false}
          />
        </Field>
        <Field title={i18n.t('filter.filter_to')}>
          <DatePicker
            key={'to' + props.searchKey}
            value={props.to}
            withTime={true}
            displaySeconds={true}
            onDateChanged={props.onToChanged}
            isFormInput={false}
          />
        </Field>
        <Field title={i18n.t('filter.error')}>
          <Input
            key={'error' + props.searchKey}
            placeholder={i18n.t('filter.search_placeholder')}
            onBlur={(e) => props.onSearchByErrorChanged(e.target.value)}
            onEnterPressed={(value) => props.onSearchByErrorChanged(value)}
            defaultValue={props.searchByError ?? ''}
            variant={'normal'}
          />
        </Field>
        <div>
          <div className={styles.buttonContainer}>
            <Button
              color={ButtonColor.Primary}
              title={i18n.t('refresh_button')}
              onClick={props.onReset}
            />
          </div>
        </div>
      </div>
    );
  },
);
